import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield, Loader2, FileText, Inbox, ChevronRight, Search, Plus,
  Camera, Gavel, AlertCircle,
} from "lucide-react";
import api from "@/lib/api";
import { relativeTime } from "@/lib/utils-app";
import { toast } from "sonner";

const STATUS_BADGE = {
  under_utredning: { label: "Under utredning", classes: "bg-blue-50 text-blue-700 border-blue-200" },
  kommunicering: { label: "Kommunicering", classes: "bg-amber-50 text-amber-700 border-amber-200" },
  beslut: { label: "Beslut", classes: "bg-violet-50 text-violet-700 border-violet-200" },
  uppfoljning: { label: "Uppföljning", classes: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  avslutat: { label: "Avslutat", classes: "bg-zinc-100 text-zinc-700 border-zinc-200" },
};

const fmt = (n) => (n || 0).toLocaleString("sv-SE").replace(/,/g, " ") + " kr";

export default function Tillsyn() {
  const [tab, setTab] = useState("inkomna");
  const [reports, setReports] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([api.get("/tillsyn-reports"), api.get("/tillsyn")]);
      setReports(r.data);
      setCases(c.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const convert = async (rid) => {
    try {
      const { data } = await api.post(`/tillsyn-reports/${rid}/convert`);
      toast.success(`Tillsynsärende skapat: ${data.ref}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte konvertera");
    }
  };

  const dismiss = async (rid) => {
    try {
      await api.post(`/tillsyn-reports/${rid}/dismiss`);
      toast.success("Anmälan avvisad");
      load();
    } catch { toast.error("Kunde inte avvisa"); }
  };

  const newReports = reports.filter((r) => r.status === "ny");
  const pagaende = cases.filter((c) => c.status !== "avslutat");
  const avslutade = cases.filter((c) => c.status === "avslutat");
  const sanktionCases = cases.filter((c) => c.sanction_calculation);
  const utanFalt = pagaende.filter((c) => !(c.inspection_photos && c.inspection_photos.length));
  const totalSanktion = sanktionCases.reduce((sum, c) => sum + (c.sanction_calculation?.total || 0), 0);

  const filterFn = (c) => !q || c.title.toLowerCase().includes(q.toLowerCase())
    || c.address?.toLowerCase().includes(q.toLowerCase())
    || c.ref?.toLowerCase().includes(q.toLowerCase());

  const activeList = tab === "pagaende" ? pagaende
    : tab === "avslutade" ? avslutade
    : tab === "sanktion" ? sanktionCases : [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Tillsyn enligt PBL</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
            Tillsynsärenden
          </h1>
          <p className="mt-2 text-zinc-500">Hantera inkomna anmälningar och driv tillsynsärenden enligt 15-stegsmodellen.</p>
        </div>
        <button onClick={() => setNewOpen(true)} data-testid="new-tillsyn"
          className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2.5 rounded-sm text-sm font-medium">
          <Plus className="w-4 h-4" /> Nytt tillsynsärende
        </button>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Pågående" value={pagaende.length} icon={FileText} color="blue" testid="stat-pagaende" />
        <StatCard label="Nya anmälningar" value={newReports.length} icon={Inbox} color="amber" testid="stat-inkomna" />
        <StatCard label="Utan fältkontroll" value={utanFalt.length} icon={AlertCircle} color="orange"
                  hint="Pågående ärenden utan foton" testid="stat-utan-falt" />
        <StatCard label="Sanktion beräknad" value={sanktionCases.length} icon={Gavel} color="red"
                  subValue={sanktionCases.length ? fmt(totalSanktion) : null}
                  testid="stat-sanktion" />
      </div>

      {/* Tabs */}
      <div className="mt-8 border-b border-zinc-200 flex gap-1 overflow-x-auto">
        {[
          { k: "inkomna", label: "Inkomna anmälningar", count: newReports.length, icon: Inbox },
          { k: "pagaende", label: "Pågående", count: pagaende.length, icon: FileText },
          { k: "sanktion", label: "Sanktion beräknad", count: sanktionCases.length, icon: Gavel },
          { k: "avslutade", label: "Avslutade", count: avslutade.length, icon: Shield },
        ].map((t) => (
          <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
              tab === t.k ? "border-blue-700 text-blue-700" : "border-transparent text-zinc-600 hover:text-zinc-900"
            }`}>
            <t.icon className="w-4 h-4 stroke-[1.5]" />
            {t.label}
            <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-sm ${tab === t.k ? "bg-blue-700 text-white" : "bg-zinc-100 text-zinc-700"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <div className="mt-6 space-y-3">
          {tab === "inkomna" && (
            newReports.length === 0 ? (
              <EmptyBox>Inga nya anmälningar.</EmptyBox>
            ) : newReports.map((r) => (
              <div key={r.report_id} data-testid={`report-${r.report_id}`} className="bg-white border border-zinc-200 rounded-sm p-5">
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-500">{r.ref}</div>
                    <div className="mt-1 text-base font-medium text-zinc-900">{r.address}</div>
                    {r.fastighet && <div className="text-xs text-zinc-500">{r.fastighet}</div>}
                    <p className="mt-2 text-sm text-zinc-700 leading-relaxed">{r.description}</p>
                    <div className="mt-3 flex items-center gap-3 text-xs text-zinc-500">
                      <span className="px-2 py-0.5 bg-zinc-100 rounded-sm font-medium">{r.category}</span>
                      <span>{r.anonymous ? "Anonym" : `Anmälare: ${r.reporter_name || "okänd"}`}</span>
                      <span>{relativeTime(r.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => convert(r.report_id)} data-testid={`convert-${r.report_id}`}
                      className="bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 rounded-sm text-xs font-medium">
                      Öppna tillsynsärende
                    </button>
                    <button onClick={() => dismiss(r.report_id)} data-testid={`dismiss-${r.report_id}`}
                      className="border border-zinc-300 hover:bg-zinc-100 text-zinc-700 px-3 py-2 rounded-sm text-xs font-medium">
                      Avvisa
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}

          {(tab === "pagaende" || tab === "avslutade" || tab === "sanktion") && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input data-testid="tillsyn-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Sök tillsynsärende..."
                  className="w-full pl-10 pr-3 py-2.5 border border-zinc-300 rounded-sm bg-white outline-none focus:border-blue-700 text-sm" />
              </div>
              {activeList.filter(filterFn).map((c) => <TillsynCard key={c.tillsyn_id} c={c} />)}
              {activeList.length === 0 && (
                <EmptyBox>Inga ärenden i denna kategori.</EmptyBox>
              )}
            </>
          )}
        </div>
      )}

      {newOpen && <NewTillsynModal onClose={() => setNewOpen(false)} onCreated={(c) => { setNewOpen(false); load(); toast.success(`Skapat: ${c.ref}`); }} />}
    </div>
  );
}


function StatCard({ label, value, icon: Icon, color, subValue, hint, testid }) {
  const colorMap = {
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    orange: "bg-orange-50 text-orange-700",
    red: "bg-red-50 text-red-700",
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{label}</div>
          <div className="mt-1.5 text-3xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>{value}</div>
          {subValue && <div className="mt-0.5 text-xs text-zinc-500">{subValue}</div>}
          {hint && <div className="mt-0.5 text-[11px] text-zinc-400">{hint}</div>}
        </div>
        <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${colorMap[color]}`}>
          <Icon className="w-4 h-4 stroke-[1.5]" />
        </div>
      </div>
    </div>
  );
}


function TillsynCard({ c }) {
  const s = STATUS_BADGE[c.status] || STATUS_BADGE.under_utredning;
  const photoCount = c.inspection_photos?.length || 0;
  const checklistCount = (c.inspection_checklist || []).filter((it) => it.checked).length;
  const sanction = c.sanction_calculation;
  return (
    <Link to={`/tillsyn/${c.tillsyn_id}`} data-testid={`tillsyn-${c.tillsyn_id}`}
      className="group block bg-white border border-zinc-200 rounded-sm p-5 hover:border-blue-400 transition">
      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-500">{c.ref}</div>
          <h3 className="mt-1 font-medium text-zinc-900 group-hover:text-blue-700 transition">{c.title}</h3>
          <div className="text-xs text-zinc-500 mt-0.5">{c.address}</div>
          <div className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
            <span className={`px-2 py-0.5 border rounded-sm font-medium ${s.classes}`}>{s.label}</span>
            {c.outcome_label && <span className="text-emerald-700 font-medium">→ {c.outcome_label}</span>}
            {!c.outcome && <span>Steg {c.current_step}/15</span>}
            {photoCount > 0 && (
              <span className="inline-flex items-center gap-1 text-blue-700">
                <Camera className="w-3 h-3" /> {photoCount}
              </span>
            )}
            {checklistCount > 0 && (
              <span className="text-amber-700">☑ {checklistCount}</span>
            )}
            {sanction && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 border border-red-200 text-red-700 font-medium rounded-sm">
                <Gavel className="w-3 h-3" /> {fmt(sanction.total)}
              </span>
            )}
            <span>· {relativeTime(c.updated_at)}</span>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-blue-700" />
      </div>
    </Link>
  );
}


function EmptyBox({ children }) {
  return <div className="bg-white border border-zinc-200 rounded-sm py-16 text-center text-sm text-zinc-500">{children}</div>;
}


function NewTillsynModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", address: "", fastighet: "", category: "olovligt", source: "egen_upptackt", description: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/tillsyn", form);
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte skapa");
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-sm p-6 animate-fade-in-up">
        <h3 className="text-xl font-medium mb-5" style={{ fontFamily: "Outfit" }}>Nytt tillsynsärende</h3>
        <form onSubmit={submit} className="space-y-3">
          <input required value={form.title} onChange={set("title")} placeholder="Titel" data-testid="nt-title"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          <input required value={form.address} onChange={set("address")} placeholder="Adress" data-testid="nt-address"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          <input value={form.fastighet} onChange={set("fastighet")} placeholder="Fastighetsbeteckning (valfri)" data-testid="nt-fastighet"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          <select value={form.source} onChange={set("source")} data-testid="nt-source"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm bg-white outline-none focus:border-blue-700 text-sm">
            <option value="egen_upptackt">Källa: Egen upptäckt</option>
            <option value="klagomal">Källa: Klagomål</option>
            <option value="anmalan">Källa: Anmälan</option>
          </select>
          <textarea rows={3} value={form.description} onChange={set("description")} placeholder="Kort beskrivning" data-testid="nt-description"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm resize-none" />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium hover:bg-zinc-100 rounded-sm">Avbryt</button>
            <button type="submit" disabled={loading} data-testid="nt-submit"
              className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm flex items-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Skapa"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
